// =============================================================================
// Layer 1 — threshold evaluation.
//
// For a given marker value + user life stage, returns which zone the value
// falls in. This is the input to Layer 2 pattern classification.
//
// All values assumed to be in internal units (see types.ts):
//   - ferritin: μg/L
//   - hemoglobin: g/L
//   - tsat: percent
//   - crp: mg/L
// =============================================================================

import {
  FERRITIN_THRESHOLDS,
  HEMOGLOBIN_THRESHOLDS,
  INFLAMMATION_THRESHOLDS,
  MCV_THRESHOLDS,
  OVERLOAD_TRIGGERS,
  RDW_THRESHOLDS,
  SEVERITY_THRESHOLDS,
  TSAT_THRESHOLDS,
  type LifeStageKey,
} from './spec.js';
import type { LabInputs, LifeStage } from './types.js';

/** Ferritin zone classification — five stages plus "elevated" for overload territory. */
export type FerritinZone =
  | 'stage_0_replete'
  | 'stage_1_incipient'
  | 'stage_2_depletion'
  | 'stage_3_ide'
  | 'stage_4_ida_range' // low enough that pairing with low Hb = IDA
  | 'elevated_normal' // above replete but below overload trigger
  | 'overload_trigger' // ferritin + tsat both above trigger
  | 'very_high'; // > 1000, regardless of TSAT

/** Hemoglobin zone relative to life-stage anemia threshold. */
export type HemoglobinZone =
  | 'within_reference' // at or above anemia threshold
  | 'mild_anemia' // between moderate and anemia threshold
  | 'moderate_anemia' // 70–99 g/L
  | 'severe_anemia' // < 70 g/L — urgent
  | 'severe_postpartum' // < 80 g/L postpartum — urgent;
  | 'transfusion_territory'; // < 50 g/L

/** TSAT zone. */
export type TSATZone = 'low' | 'normal' | 'overload_trigger';

/** MCV zone. */
export type MCVZone = 'microcytic' | 'normocytic' | 'macrocytic';

/** RDW zone. */
export type RDWZone = 'normal' | 'elevated';

/** CRP inflammation zone. */
export type CRPZone = 'no_inflammation' | 'mild_inflammation' | 'high_inflammation';

/**
 * Map LifeStage (from LabInputs) to the threshold spec key.
 * Some life stages (`none_of_these`, GAHT-specific) fall back to a default.
 */
export function mapLifeStageToSpecKey(lifeStage: LifeStage): LifeStageKey {
  switch (lifeStage) {
    case 'non_pregnant_premenopausal':
      return 'non_pregnant_premenopausal';
    case 'pregnant_t1':
      return 'pregnant_t1';
    case 'pregnant_t2':
      return 'pregnant_t2';
    case 'pregnant_t3':
      return 'pregnant_t3';
    case 'postpartum_within_6mo':
      return 'postpartum_within_6mo';
    case 'perimenopausal':
      return 'perimenopausal';
    case 'postmenopausal':
      return 'postmenopausal';
    case 'none_of_these':
      // Default to non-pregnant premenopausal; engine surfaces a confidence caveat.
      return 'non_pregnant_premenopausal';
  }
}

// -----------------------------------------------------------------------------
// Ferritin
// -----------------------------------------------------------------------------

/**
 * Classify a ferritin value into a zone, given life stage.
 * Returns a specific zone for the life-stage thresholds; callers check
 * overload-trigger conditions separately since those depend on TSAT.
 */
export function classifyFerritin(
  ferritin: number,
  lifeStage: LifeStage,
  age?: number,
): FerritinZone {
  if (ferritin > 1000) return 'very_high';

  const key = mapLifeStageToSpecKey(lifeStage);
  const t = FERRITIN_THRESHOLDS[key];

  // Use stage3IDEUpper as the primary threshold. Life-stages beyond the
  // non-pregnant model don't have all five zones defined; fall through to
  // the ones they do have.
  if (key === 'non_pregnant_premenopausal') {
    const thresholds = FERRITIN_THRESHOLDS.non_pregnant_premenopausal;
    if (ferritin >= thresholds.stage0ReleteMin) return 'stage_0_replete';
    if (ferritin >= thresholds.stage1IncipientLower) return 'stage_1_incipient';
    if (ferritin >= thresholds.stage2DepletionLower) return 'stage_2_depletion';
    if (ferritin < thresholds.stage3IDEUpper) {
      // Distinguish stage_3 (still above very-low) from stage_4_ida_range
      // (very low, likely with anemia).
      return ferritin < 15 ? 'stage_4_ida_range' : 'stage_3_ide';
    }
  }

  // Pregnancy + postpartum + peri + postmeno use single-threshold model.
  // Postmenopausal carries an age-stratified pair (Foy 2019 + Means 2024
  // reference-range work). Default to the age-50 threshold (22) when age
  // is unknown or below 70 — this is the more conservative pick of the
  // two and is anchored by Foy 2019's hematologic-response analysis.
  let ideUpper: number;
  if (key === 'postmenopausal') {
    const post = FERRITIN_THRESHOLDS.postmenopausal;
    ideUpper = age !== undefined && age >= 70
      ? post.stage3IDEUpperAtAge70plus
      : post.stage3IDEUpperAtAge50;
  } else {
    ideUpper = (t as { stage3IDEUpper: number }).stage3IDEUpper;
  }

  if (ferritin < ideUpper) {
    return ferritin < 15 ? 'stage_4_ida_range' : 'stage_3_ide';
  }

  // Above the IDE threshold but below overload — treat as elevated-normal.
  return 'elevated_normal';
}

/** True when ferritin + TSAT pair meets the overload-trigger condition for the life stage. */
export function isOverloadTrigger(
  ferritin: number,
  tsat: number,
  lifeStage: LifeStage,
): boolean {
  if (ferritin > OVERLOAD_TRIGGERS.anyStageRegardlessOfTSAT.ferritinGt) return true;

  const isPostmeno = lifeStage === 'postmenopausal';
  const trigger = isPostmeno
    ? OVERLOAD_TRIGGERS.postmenopausal
    : OVERLOAD_TRIGGERS.premenopausal;

  return ferritin > trigger.ferritinGt && tsat > trigger.tsatGt;
}

// -----------------------------------------------------------------------------
// Hemoglobin
// -----------------------------------------------------------------------------

/**
 * Classify Hb by severity zone.
 * Uses absolute severity thresholds (B7) first, then life-stage anemia threshold.
 * Postpartum users trigger `severe_postpartum` at <80 g/L per the priority-1
 * arbitration rule `urgent_postpartum_hb`.
 */
export function classifyHemoglobin(
  hemoglobin: number,
  lifeStage: LifeStage,
): HemoglobinZone {
  if (hemoglobin < SEVERITY_THRESHOLDS.transfusionTerritory) return 'transfusion_territory';
  if (hemoglobin < SEVERITY_THRESHOLDS.severeThreshold) return 'severe_anemia';

  if (
    lifeStage === 'postpartum_within_6mo' &&
    hemoglobin < SEVERITY_THRESHOLDS.severePostpartumThreshold
  ) {
    return 'severe_postpartum';
  }

  // Moderate anemia: 70 ≤ hb ≤ 99 g/L per WHO severity bands (B7).
  // Use < 100 to make the band inclusive of 99.
  if (hemoglobin < 100) return 'moderate_anemia';

  // Life-stage anemia threshold determines mild-anemia vs within-reference.
  const anemiaThreshold = anemiaThresholdForLifeStage(lifeStage);
  if (hemoglobin < anemiaThreshold) return 'mild_anemia';

  return 'within_reference';
}

/** The anemia threshold applicable to this life stage. */
export function anemiaThresholdForLifeStage(lifeStage: LifeStage): number {
  switch (lifeStage) {
    case 'pregnant_t1':
      return HEMOGLOBIN_THRESHOLDS.pregnant_t1.anemia;
    case 'pregnant_t2':
      return HEMOGLOBIN_THRESHOLDS.pregnant_t2.anemia;
    case 'pregnant_t3':
      return HEMOGLOBIN_THRESHOLDS.pregnant_t3.anemia;
    case 'postpartum_within_6mo':
      // Tool defaults to 8-week threshold (stricter). Real implementation
      // should switch to 1-week threshold if test date is < 2 weeks postpartum.
      return HEMOGLOBIN_THRESHOLDS.postpartum_8_weeks.anemia;
    case 'postmenopausal':
      // Using WHO threshold, not the evidence-suggested-raised 130.
      // Engine surfaces the "gray zone" 120–130 in the narrative for older women.
      return HEMOGLOBIN_THRESHOLDS.postmenopausal.whoThreshold;
    case 'non_pregnant_premenopausal':
    case 'perimenopausal':
    case 'none_of_these':
      return HEMOGLOBIN_THRESHOLDS.non_pregnant_premenopausal.anemia;
  }
}

// -----------------------------------------------------------------------------
// TSAT
// -----------------------------------------------------------------------------

export function classifyTSAT(tsat: number, lifeStage: LifeStage): TSATZone {
  const overloadTriggerThreshold =
    lifeStage === 'postmenopausal'
      ? TSAT_THRESHOLDS.overloadTriggerPostmenopausal
      : TSAT_THRESHOLDS.overloadTriggerPremenopausal;

  if (tsat > overloadTriggerThreshold) return 'overload_trigger';
  if (tsat < TSAT_THRESHOLDS.lowThreshold) return 'low';
  return 'normal';
}

// -----------------------------------------------------------------------------
// Red cell indices
// -----------------------------------------------------------------------------

export function classifyMCV(mcv: number): MCVZone {
  if (mcv < MCV_THRESHOLDS.microcyticThresholdFemale) return 'microcytic';
  if (mcv > MCV_THRESHOLDS.macrocyticThreshold) return 'macrocytic';
  return 'normocytic';
}

export function classifyRDW(rdw: number): RDWZone {
  return rdw >= RDW_THRESHOLDS.elevatedThreshold ? 'elevated' : 'normal';
}

// -----------------------------------------------------------------------------
// CRP / inflammation
// -----------------------------------------------------------------------------

export function classifyCRP(crp: number): CRPZone {
  if (crp >= INFLAMMATION_THRESHOLDS.crp.highInflammationThreshold) return 'high_inflammation';
  if (crp >= INFLAMMATION_THRESHOLDS.crp.inflammationThreshold) return 'mild_inflammation';
  return 'no_inflammation';
}

// -----------------------------------------------------------------------------
// Bundle: evaluate every marker present in the inputs
// -----------------------------------------------------------------------------

/**
 * Evaluated thresholds for all markers in the input set.
 * Layer 2 pattern classification reads this struct.
 */
export interface ThresholdEvaluation {
  ferritinZone?: FerritinZone;
  hemoglobinZone?: HemoglobinZone;
  tsatZone?: TSATZone;
  mcvZone?: MCVZone;
  rdwZone?: RDWZone;
  crpZone?: CRPZone;
  isOverloadTriggerFired?: boolean;
}

/** Evaluate every threshold the user's input set allows. */
export function evaluateThresholds(inputs: LabInputs): ThresholdEvaluation {
  const out: ThresholdEvaluation = {};

  if (inputs.ferritin !== undefined) {
    out.ferritinZone = classifyFerritin(inputs.ferritin, inputs.lifeStage, inputs.age);
  }
  if (inputs.hemoglobin !== undefined) {
    out.hemoglobinZone = classifyHemoglobin(inputs.hemoglobin, inputs.lifeStage);
  }
  if (inputs.tsat !== undefined) {
    out.tsatZone = classifyTSAT(inputs.tsat, inputs.lifeStage);
  }
  if (inputs.mcv !== undefined) {
    out.mcvZone = classifyMCV(inputs.mcv);
  }
  if (inputs.rdw !== undefined) {
    out.rdwZone = classifyRDW(inputs.rdw);
  }
  if (inputs.crp !== undefined) {
    out.crpZone = classifyCRP(inputs.crp);
  }

  // Overload trigger requires both ferritin AND tsat.
  if (inputs.ferritin !== undefined && inputs.tsat !== undefined) {
    out.isOverloadTriggerFired = isOverloadTrigger(
      inputs.ferritin,
      inputs.tsat,
      inputs.lifeStage,
    );
  }

  return out;
}
